import React, { useState, useEffect, useCallback } from 'react';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, RefreshCw, Edit, Trash2, FileText, FileSpreadsheet, ClipboardList, MoreVertical, ClipboardCheck, FileEdit, CheckSquare, Search, Shield, Flame, LayoutGrid, List, Archive, ArchiveRestore } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { getSystemTypeLabel, getSystemTypesByCategory, getSystemCategory } from '@/lib/systemTypeConstants';
import { getSystemTypeIcon, getSystemTypeColor } from '@/lib/systemTypeIcons';
import { BackButton } from '@/components/BackButton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ImportarInventarioDialog } from '@/components/inventario/ImportarInventarioDialog';
import { InformeMultiSistemaDialog } from '@/components/anomalias/InformeMultiSistemaDialog';
import { SedeFormDialog } from '@/components/cliente/SedeFormDialog';

import { hasChecklistTemplate, getChecklistTemplate } from '@/lib/checklists/registry';
import { filtrarPorTipoRevision, type TipoRevision } from '@/lib/checklists/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, MapPin } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CoberturaRevisionesCard } from '@/components/sistemas/CoberturaRevisionesCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface SistemaRevisionMeta {
  ultimoFinalizado?: { fecha: string; tipo_revision: string; periodo: string | null };
  borrador?: { id: string; tipo_revision: string; fecha: string; periodo: string | null; progreso: number };
  ultimaRevisionElementos?: { fecha: string; periodo: string | null; tipo_revision: string | null };
  borradorElementos?: { id: string; fecha: string; periodo: string | null; tipo_revision: string | null };
}

const TIPO_REVISION_LETTER: Record<string, 'T' | 'S' | 'A'> = {
  trimestral: 'T',
  semestral: 'S',
  anual: 'A',
};

const ESTADOS_FINALIZADOS = ['finalizado', 'completado', 'finalizada'];

const formatMMYYYY = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

/** Formatea periodo MM/AAAA priorizando el periodo introducido por el técnico. */
const formatPeriodo = (periodo?: string | null, fallbackIso?: string | null): string => {
  if (periodo && /^(\d{4})-(\d{2})$/.test(periodo)) {
    return `${periodo.slice(5)}/${periodo.slice(0, 4)}`;
  }
  return formatMMYYYY(fallbackIso);
};


const totalPreguntasTemplate = (tipoSistema: string, tipo: TipoRevision): number => {
  const t = getChecklistTemplate(tipoSistema);
  if (!t) return 0;
  return t.protocolos.reduce(
    (acc, p) => acc + filtrarPorTipoRevision(p.preguntas, tipo).length,
    0
  );
};


const SistemasCliente = () => {
  const navigate = useNavigate();
  const { 
    sistemas, 
    sistemasArchivados,
    sedes,
    sedeActual,
    setSedeActual,
    clienteActual, 
    setSistemaActual,
    cargarSistemas, 
    crearSistema,
    editarSistema,
    archivarSistema,
    restaurarSistema,
    eliminarSistemaDefinitivo,
    isLoading 
  } = useSecurityContext();

  const [dialogAbierto, setDialogAbierto] = useState(false);
  const [sistemaEditando, setSistemaEditando] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [informeCategoria, setInformeCategoria] = useState<'seguridad' | 'pci' | null>(null);
  const [sedeDialogOpen, setSedeDialogOpen] = useState(false);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [sedeEditando, setSedeEditando] = useState<any>(null);
  const irARevision = useCallback((sistema: any, checklistId?: string) => {
    if (!clienteActual) return;
    const params = new URLSearchParams({
      sistemaId: sistema.id,
      sistemaTipo: sistema.tipo,
      clienteId: clienteActual.id,
      clienteNombre: clienteActual.nombre || '',
      returnTo: window.location.pathname + window.location.search,
    });
    if (checklistId) params.set('checklistId', checklistId);
    navigate(`/checklist-revision?${params.toString()}`);
  }, [clienteActual, navigate]);
  const [borradoresPorSistema, setBorradoresPorSistema] = useState<Record<string, string>>({});
  const [revisionMetaPorSistema, setRevisionMetaPorSistema] = useState<Record<string, SistemaRevisionMeta>>({});
  const { perfil, hasRole } = useAuth();
  const puedeBorrarDefinitivo = hasRole('admin') || hasRole('super_admin');
  const [formData, setFormData] = useState({
    tipo: '',
    descripcion: '',
    ubicacion: '',
    categoria: 'seguridad' as 'seguridad' | 'pci'
  });
  const [activeTab, setActiveTab] = useState<'seguridad' | 'pci'>('seguridad');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    if (typeof window === 'undefined') return 'grid';
    return (localStorage.getItem('sistemas_view_mode') as 'grid' | 'list') || 'grid';
  });
  useEffect(() => {
    localStorage.setItem('sistemas_view_mode', viewMode);
  }, [viewMode]);

  const sedesCliente = React.useMemo(
    () => sedes.filter(s => s.cliente_id === clienteActual?.id),
    [sedes, clienteActual?.id]
  );

  // Cargar última revisión finalizada y borrador (con progreso) por sistema.
  const cargarBorradores = useCallback(async () => {
    if (!clienteActual) return;
    const { data } = await supabase
      .from('checklists_seguridad')
      .select('id, sistema_id, tecnico_id, estado, tipo_revision, fecha, updated_at, modo, periodo')
      .eq('cliente_id', clienteActual.id)
      .eq('archivado', false)
      .not('sistema_id', 'is', null)
      .order('updated_at', { ascending: false });

    const borradoresMap: Record<string, string> = {};
    const metaMap: Record<string, SistemaRevisionMeta> = {};
    const borradorIds: { id: string; sistema_id: string; tipo_revision: string }[] = [];

    (data || []).forEach((row: any) => {
      if (!row.sistema_id) return;
      const meta = metaMap[row.sistema_id] || {};
      const fecha = row.updated_at || row.fecha;
      const periodo = (row.periodo as string | null) ?? null;
      const esRapida = row.modo === 'elementos' || row.tipo_revision === 'rapida';
      const esBorrador = row.estado === 'en_progreso';
      const esFinalizado = ESTADOS_FINALIZADOS.includes(row.estado);

      if (esRapida) {
        if (esFinalizado && !meta.ultimaRevisionElementos) {
          meta.ultimaRevisionElementos = { fecha, periodo, tipo_revision: row.tipo_revision ?? null };
        }
        if (esBorrador && !meta.borradorElementos && row.tecnico_id === perfil?.user_id) {
          meta.borradorElementos = { id: row.id, fecha, periodo, tipo_revision: row.tipo_revision ?? null };
        }
      } else {
        if (esFinalizado && !meta.ultimoFinalizado) {
          meta.ultimoFinalizado = { fecha, tipo_revision: row.tipo_revision, periodo };
        }
        if (esBorrador && !meta.borrador && row.tecnico_id === perfil?.user_id) {
          meta.borrador = { id: row.id, tipo_revision: row.tipo_revision, fecha, periodo, progreso: 0 };
          borradoresMap[row.sistema_id] = row.id;
          borradorIds.push({ id: row.id, sistema_id: row.sistema_id, tipo_revision: row.tipo_revision });
        }
      }
      metaMap[row.sistema_id] = meta;
    });


    // Calcular progreso por borrador
    if (borradorIds.length > 0) {
      const ids = borradorIds.map(b => b.id);
      const { data: resp } = await supabase
        .from('checklist_respuestas')
        .select('checklist_id, pregunta_id, respuesta')
        .in('checklist_id', ids);

      const respondidasPorChecklist: Record<string, Set<string>> = {};
      (resp || []).forEach((r: any) => {
        if (!r.respuesta) return;
        if (!respondidasPorChecklist[r.checklist_id]) respondidasPorChecklist[r.checklist_id] = new Set();
        respondidasPorChecklist[r.checklist_id].add(r.pregunta_id);
      });

      borradorIds.forEach(b => {
        const sistema = sistemas.find(s => s.id === b.sistema_id);
        if (!sistema) return;
        const total = totalPreguntasTemplate(sistema.tipo, (b.tipo_revision || 'trimestral') as TipoRevision);
        const respondidas = respondidasPorChecklist[b.id]?.size || 0;
        const progreso = total > 0 ? Math.min(100, Math.round((respondidas / total) * 100)) : 0;
        if (metaMap[b.sistema_id]?.borrador) {
          metaMap[b.sistema_id].borrador!.progreso = progreso;
        }
      });
    }

    setBorradoresPorSistema(borradoresMap);
    setRevisionMetaPorSistema(metaMap);
  }, [clienteActual?.id, perfil?.user_id, sistemas]);

  useEffect(() => { cargarBorradores(); }, [cargarBorradores]);


  // Auto-seleccionar primera sede al cambiar de cliente
  React.useEffect(() => {
    if (!clienteActual) return;
    if (sedeActual && sedeActual.cliente_id === clienteActual.id) return;
    setSedeActual(sedesCliente[0] || null);
  }, [clienteActual, sedesCliente, sedeActual, setSedeActual]);

  const sistemasDelCliente = sistemas.filter(
    sistema => sistema.cliente_id === clienteActual?.id
      && (sedeActual ? sistema.sede_id === sedeActual.id : !sistema.sede_id)
  );

  const archivadosDelCliente = sistemasArchivados.filter(
    sistema => sistema.cliente_id === clienteActual?.id
      && (sedeActual ? sistema.sede_id === sedeActual.id : !sistema.sede_id)
  );

  const sistemasVisibles = mostrarArchivados
    ? [...sistemasDelCliente, ...archivadosDelCliente]
    : sistemasDelCliente;

  const sistemasPorCategoria = {
    seguridad: sistemasVisibles.filter(s => (s.categoria || 'seguridad') === 'seguridad'),
    pci: sistemasVisibles.filter(s => s.categoria === 'pci')
  };

  const hasSeguridad = sistemasPorCategoria.seguridad.length > 0;
  const hasPci = sistemasPorCategoria.pci.length > 0;
  // Mostrar solo categorías presentes; si no hay ninguna, mostrar ambas para permitir crear.
  const showSeguridad = hasSeguridad || (!hasSeguridad && !hasPci);
  const showPci = hasPci || (!hasSeguridad && !hasPci);

  // Asegurar que activeTab apunta a una pestaña visible
  useEffect(() => {
    if (activeTab === 'seguridad' && !showSeguridad && showPci) setActiveTab('pci');
    if (activeTab === 'pci' && !showPci && showSeguridad) setActiveTab('seguridad');
  }, [showSeguridad, showPci, activeTab]);

  useEffect(() => {
    if (!clienteActual) {
      navigate('/clientes');
    }
  }, [clienteActual, navigate]);

  const getIconoTipo = (tipo: string) => {
    const Icon = getSystemTypeIcon(tipo);
    return <Icon className="h-5 w-5" />;
  };

  const getColorTipo = (tipo: string, categoria?: string) =>
    getSystemTypeColor(tipo, categoria as 'seguridad' | 'pci' | undefined);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clienteActual || !sedeActual) return;

    const sistemaData = {
      ...formData,
      cliente_id: clienteActual.id,
      sede_id: sedeActual.id,
    };

    if (sistemaEditando) {
      await editarSistema(sistemaEditando, sistemaData);
    } else {
      await crearSistema(sistemaData);
    }

    setDialogAbierto(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      tipo: '',
      descripcion: '',
      ubicacion: '',
      categoria: activeTab
    });
    setSistemaEditando(null);
  };

  const abrirEdicion = (sistema: any) => {
    setFormData({
      tipo: sistema.tipo,
      descripcion: sistema.descripcion || '',
      ubicacion: sistema.ubicacion || '',
      categoria: sistema.categoria || 'seguridad'
    });
    setSistemaEditando(sistema.id);
    setDialogAbierto(true);
  };

  const handleSistemaClick = (sistema: any) => {
    setSistemaActual(sistema);
    navigate('/elementos-sistema');
  };

  // Unificado: "Revisar elementos" ahora forma parte de "Empezar revisión".

  const handleArchivar = async (id: string) => {
    await archivarSistema(id);
  };

  const handleRestaurar = async (id: string) => {
    await restaurarSistema(id);
  };

  const handleEliminarDefinitivo = async (id: string) => {
    await eliminarSistemaDefinitivo(id);
  };

  const handleEliminarBorrador = async (checklistId: string) => {
    try {
      await supabase.from('checklist_respuestas').delete().eq('checklist_id', checklistId);
      await supabase.from('checklist_elementos_probados').delete().eq('checklist_id', checklistId);
      await supabase.from('checklist_cabeceras').delete().eq('checklist_id', checklistId);
      const { error } = await supabase.from('checklists_seguridad').delete().eq('id', checklistId);
      if (error) throw error;
      await cargarBorradores();
    } catch (e) {
      console.error('Error eliminando borrador', e);
    }
  };

  if (!clienteActual) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
      <div className="max-w-6xl mx-auto mobile-spacing">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center gap-4">
            <BackButton to={`/cliente/${clienteActual.id}`} label="Volver a Detalles" />
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mobile-text">
                Sistemas de {clienteActual.nombre}
              </h1>
              <p className="text-muted-foreground text-sm md:text-base">
                {sedeActual?.direccion || clienteActual.direccion}
              </p>
            </div>
          </div>

          {/* Selector de sede */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border bg-card">
            <div className="flex items-center gap-2 text-sm font-medium shrink-0">
              <Building2 className="h-4 w-4" />
              Sede / Emplazamiento:
            </div>
            <div className="flex-1 flex flex-col sm:flex-row gap-2">
              {sedesCliente.length === 0 ? (
                <p className="text-sm text-muted-foreground flex-1">
                  Este cliente no tiene sedes. Crea una para poder gestionar sus sistemas.
                </p>
              ) : (
                <Select
                  value={sedeActual?.id || ''}
                  onValueChange={(id) => setSedeActual(sedesCliente.find(s => s.id === id) || null)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecciona una sede" />
                  </SelectTrigger>
                  <SelectContent>
                    {sedesCliente.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nombre}{s.direccion ? ` — ${s.direccion}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSedeEditando(null); setSedeDialogOpen(true); }}
                >
                  <Plus className="h-4 w-4 mr-1" /> Nueva sede
                </Button>
                {sedeActual && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setSedeEditando(sedeActual); setSedeDialogOpen(true); }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end sm:items-center">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(v) => v && setViewMode(v as 'grid' | 'list')}
              className="border rounded-md"
            >
              <ToggleGroupItem value="grid" aria-label="Vista en cuadrícula" size="sm">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="Vista en lista" size="sm">
                <List className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="mobile-button shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover z-50">
                <DropdownMenuItem onClick={() => setImportDialogOpen(true)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Importar desde Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMostrarArchivados(v => !v)}>
                  <Archive className="h-4 w-4 mr-2" />
                  {mostrarArchivados ? 'Ocultar archivados' : `Mostrar archivados${archivadosDelCliente.length ? ` (${archivadosDelCliente.length})` : ''}`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={sistemasDelCliente.length === 0}
                  className="flex items-center justify-center gap-2 mobile-button"
                >
                  <ClipboardList className="h-4 w-4" />
                  Generar Informe
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover z-50">
                <DropdownMenuItem
                  disabled={sistemasDelCliente.filter(s => (s.categoria || 'seguridad') === 'seguridad').length === 0}
                  onClick={() => setInformeCategoria('seguridad')}
                >
                  Informe Seguridad
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={sistemasDelCliente.filter(s => s.categoria === 'pci').length === 0}
                  onClick={() => setInformeCategoria('pci')}
                >
                  Informe PCI
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Dialog open={dialogAbierto} onOpenChange={setDialogAbierto}>
              <DialogTrigger asChild>
                <Button 
                  variant="default"
                  onClick={resetForm}
                  disabled={!sedeActual}
                  title={!sedeActual ? 'Crea o selecciona una sede primero' : ''}
                  className="flex items-center justify-center gap-2 mobile-button"
                >
                  <Plus className="h-4 w-4" />
                  Nuevo Sistema
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] mx-4 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {sistemaEditando ? 'Editar Sistema' : 'Crear Nuevo Sistema'}
                  </DialogTitle>
                  <DialogDescription>
                    {sistemaEditando 
                      ? 'Modifica los datos del sistema seleccionado.'
                      : 'Completa los datos para crear un nuevo sistema de seguridad.'
                    }
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="categoria">Categoría</Label>
                    <Select 
                      value={formData.categoria} 
                      onValueChange={(value) => setFormData({...formData, categoria: value as 'seguridad' | 'pci', tipo: ''})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona la categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seguridad">Seguridad</SelectItem>
                        <SelectItem value="pci">PCI (Protección Contra Incendios)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tipo">Tipo de Sistema</Label>
                    <Select 
                      value={formData.tipo} 
                      onValueChange={(value) => setFormData({...formData, tipo: value})}
                      disabled={!formData.categoria}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona el tipo de sistema" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(getSystemTypesByCategory(formData.categoria)).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="descripcion">Descripción</Label>
                    <Textarea
                      id="descripcion"
                      value={formData.descripcion}
                      onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                      placeholder="Descripción del sistema..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ubicacion">Ubicación</Label>
                    <Input
                      id="ubicacion"
                      value={formData.ubicacion}
                      onChange={(e) => setFormData({...formData, ubicacion: e.target.value})}
                      placeholder="Ubicación del sistema..."
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogAbierto(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" variant="default">
                      {sistemaEditando ? 'Actualizar' : 'Crear'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {clienteActual && (
          <div className="mb-4">
            <CoberturaRevisionesCard
              clienteId={clienteActual.id}
              sedeId={sedeActual?.id ?? null}
            />
          </div>
        )}

        {/* Tabs: solo se muestran las categorías presentes (o ambas si el cliente está vacío) */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'seguridad' | 'pci')} className="w-full">
          <TabsList
            className={`grid w-full ${showSeguridad && showPci ? 'grid-cols-2' : 'grid-cols-1'}`}
          >
            {showSeguridad && (
              <TabsTrigger value="seguridad" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Seguridad ({sistemasPorCategoria.seguridad.length})
              </TabsTrigger>
            )}
            {showPci && (
              <TabsTrigger value="pci" className="flex items-center gap-2">
                <Flame className="h-4 w-4" />
                PCI ({sistemasPorCategoria.pci.length})
              </TabsTrigger>
            )}
          </TabsList>

          {showSeguridad && (
            <TabsContent value="seguridad" className="mt-6">
              <SistemasGrid
                sistemas={sistemasPorCategoria.seguridad}
                categoria="seguridad"
                onSistemaClick={handleSistemaClick}
                onEditSistema={abrirEdicion}
                onArchiveSistema={handleArchivar}
                onRestoreSistema={handleRestaurar}
                onHardDeleteSistema={handleEliminarDefinitivo}
                puedeBorrarDefinitivo={puedeBorrarDefinitivo}
                onRevisionClick={(s, checklistId) => irARevision(s, checklistId)}
                onDeleteBorrador={handleEliminarBorrador}
                borradoresPorSistema={borradoresPorSistema}
                revisionMetaPorSistema={revisionMetaPorSistema}
                onAddSistema={() => {
                  setFormData(prev => ({ ...prev, categoria: 'seguridad' }));
                  setDialogAbierto(true);
                }}
                getIconoTipo={getIconoTipo}
                getColorTipo={getColorTipo}
                viewMode={viewMode}
              />
            </TabsContent>
          )}

          {showPci && (
            <TabsContent value="pci" className="mt-6">
              <SistemasGrid
                sistemas={sistemasPorCategoria.pci}
                categoria="pci"
                onSistemaClick={handleSistemaClick}
                onEditSistema={abrirEdicion}
                onArchiveSistema={handleArchivar}
                onRestoreSistema={handleRestaurar}
                onHardDeleteSistema={handleEliminarDefinitivo}
                puedeBorrarDefinitivo={puedeBorrarDefinitivo}
                onRevisionClick={(s, checklistId) => irARevision(s, checklistId)}
                onDeleteBorrador={handleEliminarBorrador}
                borradoresPorSistema={borradoresPorSistema}
                revisionMetaPorSistema={revisionMetaPorSistema}
                onAddSistema={() => {
                  setFormData(prev => ({ ...prev, categoria: 'pci' }));
                  setDialogAbierto(true);
                }}
                getIconoTipo={getIconoTipo}
                getColorTipo={getColorTipo}
                viewMode={viewMode}
              />
            </TabsContent>
          )}
        </Tabs>


        <ImportarInventarioDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          onSuccess={() => cargarSistemas()}
          clienteId={clienteActual?.id}
          clienteNombre={clienteActual?.nombre}
          sedeId={sedeActual?.id}
          sedeNombre={sedeActual?.nombre}
        />

        <InformeMultiSistemaDialog
          open={informeCategoria !== null}
          onOpenChange={(o) => { if (!o) setInformeCategoria(null); }}
          sistemas={sistemasDelCliente}
          clienteNombre={clienteActual?.nombre}
          clienteId={clienteActual?.id}
          sede={sedeActual}
          categoria={informeCategoria ?? undefined}
        />

        {clienteActual && (
          <SedeFormDialog
            open={sedeDialogOpen}
            onOpenChange={setSedeDialogOpen}
            clienteId={clienteActual.id}
            sede={sedeEditando}
            onSaved={(s) => setSedeActual(s)}
          />
        )}
      </div>
    </div>
  );
};

// Componente separado para la grilla de sistemas
const SistemaArchiveButton = ({ sistema, onConfirm }: { sistema: any; onConfirm: (id: string) => void }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="sm" title="Archivar sistema" onClick={(e) => e.stopPropagation()}>
        <Archive className="h-4 w-4" />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
      <AlertDialogHeader>
        <AlertDialogTitle>¿Archivar sistema?</AlertDialogTitle>
        <AlertDialogDescription>
          El sistema "{getSystemTypeLabel(sistema.tipo)}" dejará de aparecer en listados, selectores y contadores, pero se conservan su inventario, revisiones e informes. Podrás restaurarlo cuando quieras.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction onClick={() => onConfirm(sistema.id)}>Archivar</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const SistemaHardDeleteButton = ({ sistema, onConfirm }: { sistema: any; onConfirm: (id: string) => void }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="sm" title="Eliminar definitivamente" onClick={(e) => e.stopPropagation()}>
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
      <AlertDialogHeader>
        <AlertDialogTitle>¿Eliminar definitivamente?</AlertDialogTitle>
        <AlertDialogDescription>
          Esta acción no se puede deshacer. Solo se permite si el sistema "{getSystemTypeLabel(sistema.tipo)}" no tiene inventario, revisiones ni informes asociados.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction
          onClick={() => onConfirm(sistema.id)}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          Eliminar
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const SistemasGrid = ({ 
  sistemas, 
  categoria, 
  onSistemaClick, 
  onEditSistema, 
  onArchiveSistema, 
  onRestoreSistema,
  onHardDeleteSistema,
  puedeBorrarDefinitivo = false,
  onAddSistema,
  onRevisionClick,
  onDeleteBorrador,
  borradoresPorSistema,
  revisionMetaPorSistema,
  getIconoTipo,
  getColorTipo,
  viewMode = 'grid',
}: {
  sistemas: any[];
  categoria: 'seguridad' | 'pci';
  onSistemaClick: (sistema: any) => void;
  onEditSistema: (sistema: any) => void;
  onArchiveSistema: (id: string) => void;
  onRestoreSistema: (id: string) => void;
  onHardDeleteSistema: (id: string) => void;
  puedeBorrarDefinitivo?: boolean;
  onAddSistema: () => void;
  onRevisionClick: (sistema: any, checklistId?: string) => void;
  onDeleteBorrador: (checklistId: string) => void | Promise<void>;
  borradoresPorSistema: Record<string, string>;
  revisionMetaPorSistema: Record<string, SistemaRevisionMeta>;
  getIconoTipo: (tipo: string) => React.ReactNode;
  getColorTipo: (tipo: string, categoria?: string) => string;
  viewMode?: 'grid' | 'list';
}) => {
  const [confirmNuevo, setConfirmNuevo] = React.useState<null | { sistemaId: string; borradorId: string; sistema: any }>(null);
  const categoryLabels = {
    seguridad: 'seguridad',
    pci: 'protección contra incendios'
  };

  const containerClass = viewMode === 'list'
    ? 'flex flex-col gap-2'
    : 'grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={containerClass}>
      {sistemas.length === 0 ? (
        <Card className="col-span-full">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hay sistemas de {categoryLabels[categoria]} registrados</h3>
            <p className="text-muted-foreground text-center mb-4">
              Este cliente no tiene sistemas de {categoryLabels[categoria]} registrados aún.
            </p>
            <Button 
              variant="default"
              onClick={onAddSistema}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Agregar Primer Sistema
            </Button>
          </CardContent>
        </Card>
      ) : (
        sistemas.map((sistema) => {
          const meta = revisionMetaPorSistema[sistema.id];
          const borradorId = borradoresPorSistema[sistema.id];
          const tieneChecklist = hasChecklistTemplate(sistema.tipo);
          const archivado = sistema.activo === false;

          if (viewMode === 'list') {
            return (
              <div
                key={sistema.id}
                className={`group flex items-center gap-3 p-3 rounded-lg border bg-card hover:shadow-md hover:border-primary/50 transition-all cursor-pointer ${archivado ? 'opacity-60 border-dashed' : ''}`}
                onClick={() => onSistemaClick(sistema)}
              >
                <div className={`p-2 rounded-md ${archivado ? 'bg-muted text-muted-foreground' : `${getColorTipo(sistema.tipo, sistema.categoria)} text-white`} shrink-0`}>
                  {getIconoTipo(sistema.tipo)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{getSystemTypeLabel(sistema.tipo)}</span>
                    <Badge variant="secondary" className="text-xs">
                      {sistema.ubicacion || 'Sin ubicación'}
                    </Badge>
                    {archivado && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <Archive className="h-3 w-3" /> Archivado
                      </Badge>
                    )}
                  </div>
                  {sistema.descripcion && (
                    <p className="text-xs text-muted-foreground truncate">{sistema.descripcion}</p>
                  )}
                </div>
                <div className="hidden md:flex items-center gap-1.5 shrink-0">
                  {meta?.ultimoFinalizado && (
                    <Badge variant="outline" className="gap-1 border-green-500/40 text-green-700 dark:text-green-400 text-xs">
                      <ClipboardCheck className="h-3 w-3" />
                      {formatPeriodo(meta.ultimoFinalizado.periodo, meta.ultimoFinalizado.fecha)}
                      <span className="ml-0.5 font-bold">
                        {TIPO_REVISION_LETTER[meta.ultimoFinalizado.tipo_revision] || '?'}
                      </span>
                    </Badge>
                  )}
                  {meta?.borrador && (
                    <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500 border-amber-600 text-xs">
                      <FileEdit className="h-3 w-3" />
                      {meta.borrador.progreso}%
                    </Badge>
                  )}
                </div>
                {tieneChecklist && !archivado && (
                  <Button
                    variant={borradorId ? 'default' : 'outline'}
                    size="sm"
                    className="gap-1 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRevisionClick(sistema, borradorId);
                    }}
                  >
                    {borradorId ? <FileEdit className="h-4 w-4" /> : <ClipboardCheck className="h-4 w-4" />}
                    <span className="hidden sm:inline">{borradorId ? 'Continuar' : 'Revisar'}</span>
                  </Button>
                )}
                <div className="flex gap-0.5 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  {archivado ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Restaurar sistema"
                        onClick={(e) => { e.stopPropagation(); onRestoreSistema(sistema.id); }}
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </Button>
                      {puedeBorrarDefinitivo && (
                        <SistemaHardDeleteButton sistema={sistema} onConfirm={onHardDeleteSistema} />
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onEditSistema(sistema); }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <SistemaArchiveButton sistema={sistema} onConfirm={onArchiveSistema} />
                    </>
                  )}
                </div>
              </div>
            );
          }

          return (
          <Card
            key={sistema.id}
            className={`cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary/50 group ${archivado ? 'opacity-60 border-dashed' : ''}`}
            onClick={() => onSistemaClick(sistema)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${archivado ? 'bg-muted text-muted-foreground' : `${getColorTipo(sistema.tipo, sistema.categoria)} text-white`}`}>
                    {getIconoTipo(sistema.tipo)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{getSystemTypeLabel(sistema.tipo)}</CardTitle>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      <Badge variant="secondary">
                        {sistema.ubicacion || 'Sin ubicación'}
                      </Badge>
                      {archivado && (
                        <Badge variant="outline" className="gap-1">
                          <Archive className="h-3 w-3" /> Archivado
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  {archivado ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Restaurar sistema"
                        onClick={(e) => { e.stopPropagation(); onRestoreSistema(sistema.id); }}
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </Button>
                      {puedeBorrarDefinitivo && (
                        <SistemaHardDeleteButton sistema={sistema} onConfirm={onHardDeleteSistema} />
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditSistema(sistema);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <SistemaArchiveButton sistema={sistema} onConfirm={onArchiveSistema} />
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <CardDescription className="text-sm">
                {sistema.descripcion || 'Sin descripción disponible'}
              </CardDescription>

              {/* Estado revisión unificada (checklist + elementos en la misma página) */}
              {tieneChecklist && !archivado && (
                  <div className="space-y-2">
                    {(meta?.ultimoFinalizado || meta?.borrador) && (
                      <div className="flex flex-wrap gap-2">
                        {meta?.ultimoFinalizado && (
                          <Badge variant="outline" className="gap-1 border-green-500/40 text-green-700 dark:text-green-400">
                            <ClipboardCheck className="h-3 w-3" />
                            Última: {formatPeriodo(meta.ultimoFinalizado.periodo, meta.ultimoFinalizado.fecha)}
                            <span className="ml-1 font-bold">
                              {TIPO_REVISION_LETTER[meta.ultimoFinalizado.tipo_revision] || '?'}
                            </span>
                          </Badge>
                        )}
                        {meta?.borrador && (
                          <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500 border-amber-600 shadow-sm">
                            <FileEdit className="h-3 w-3" />
                            Borrador {TIPO_REVISION_LETTER[meta.borrador.tipo_revision] || '?'}: {formatPeriodo(meta.borrador.periodo, meta.borrador.fecha)} · {meta.borrador.progreso}%
                            <button
                              type="button"
                              className="ml-1 -mr-1 rounded hover:bg-white/20 p-0.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('¿Eliminar borrador de revisión?')) {
                                  onDeleteBorrador(meta.borrador!.id);
                                }
                              }}
                              aria-label="Eliminar borrador"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </Badge>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        variant={borradorId ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRevisionClick(sistema, borradorId);
                        }}
                      >
                        {borradorId ? (
                          <>
                            <FileEdit className="h-4 w-4" />
                            Continuar revisión
                          </>
                        ) : (
                          <>
                            <ClipboardCheck className="h-4 w-4" />
                            Empezar revisión
                          </>
                        )}
                      </Button>
                      {borradorId && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmNuevo({ sistemaId: sistema.id, borradorId, sistema });
                          }}
                          title="Empezar nueva revisión (eliminará el borrador)"
                        >
                          <Plus className="h-4 w-4" />
                          Nueva
                        </Button>
                      )}
                    </div>
                  </div>
              )}

              <div className="text-xs text-muted-foreground">
                Clic para ver elementos →
              </div>
            </CardContent>
          </Card>
          );
        })
      )}


      <AlertDialog open={!!confirmNuevo} onOpenChange={(o) => { if (!o) setConfirmNuevo(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Empezar nueva revisión?</AlertDialogTitle>
            <AlertDialogDescription>
              Existe un borrador en curso para este sistema. Si continúas, <strong>el borrador anterior será eliminado</strong> de forma permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!confirmNuevo) return;
                const { borradorId, sistema } = confirmNuevo;
                await onDeleteBorrador(borradorId);
                setConfirmNuevo(null);
                onRevisionClick(sistema, undefined);
              }}
            >
              Eliminar y empezar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SistemasCliente;